import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import AppSelect from "../../../../shared/components/AppSelect";
import Button from "../../../../shared/components/Button";
import EmptyState from "../../../../shared/components/EmptyState";
import GradientShareButton from "../../../../shared/components/GradientShareButton";
import InfoTooltip from "../../../../shared/components/InfoTooltip";
import LoadingPage from "../../../../shared/components/LoadingPage";
import OrganizerDatePicker, {
  isDateRangeInvalid,
} from "../../../../shared/components/OrganizerDatePicker";
import PlayerFiltersBar from "../../../../shared/components/PlayerFiltersBar";
import {
  isPlayerCategoryCode,
  playerCategoryLabelFromCode,
  PLAYER_CATEGORY_OPTIONS,
} from "../../../../shared/components/PlayerCategorySelect";
import { playerSexLabelFromCode } from "../../../../shared/components/PlayerSexSelect";
import ResponsiveRankingTable, {
  ResponsiveRankingColumn,
  ResponsiveRankingRow,
} from "../../../../shared/components/ResponsiveRankingTable";
import TnpOrganizerLogo, {
  isTnpOrganizer,
} from "../../../../shared/components/TnpOrganizerLogo";
import UserAvatar from "../../../../shared/components/UserAvatar";
import useBodyScrollLock from "../../../../shared/hooks/useBodyScrollLock";
import useOrganizerPageVisualIdentity from "../../../../shared/hooks/useOrganizerPageVisualIdentity";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import {
  buildRankingShareUrl,
  syncCanonicalShareUrl,
} from "../../../../shared/lib/shareLinks";
import { organizerIdentityStyle } from "../../../../shared/utils/organizerVisualIdentity";
import { listOrganizers } from "../../../auth/shared/services/authService";
import RankingShareModal from "../components/RankingShareModal";
import {
  getRankingUserHistory,
  getSuper8Ranking,
} from "../../shared/services/super8RankingService";
import {
  RankingGenderCode,
  RankingModeCode,
  RankingPeriodCode,
  RankingSourceCode,
  RankingUserHistoryItem,
  Super8RankingResponse,
} from "../../shared/types/super8Ranking";
import "./RankingPage.css";

const MODE_TABS: Array<{ value: RankingModeCode; label: string }> = [
  { value: "all", label: siteTexts.ranking.page.modeTabs.all },
  { value: "super8", label: siteTexts.ranking.page.modeTabs.super8 },
  { value: "challenge", label: siteTexts.ranking.page.modeTabs.challenge },
  { value: "poco", label: siteTexts.ranking.page.modeTabs.poco },
  { value: "tournament", label: siteTexts.ranking.page.modeTabs.tournament },
];
const SOURCE_TABS: Array<{ value: RankingSourceCode; label: string }> = [
  { value: "official", label: siteTexts.ranking.page.sourceTabs.official },
  { value: "organizer", label: siteTexts.ranking.page.sourceTabs.organizer },
];
const PERIOD_OPTIONS: Array<{
  value: RankingPeriodCode;
  label: string;
  description: string;
}> = [
  {
    value: "all_time",
    label: siteTexts.ranking.page.periodOptions.allTime.label,
    description: siteTexts.ranking.page.periodOptions.allTime.description,
  },
  {
    value: "last4",
    label: siteTexts.ranking.page.periodOptions.last4.label,
    description: siteTexts.ranking.page.periodOptions.last4.description,
  },
  {
    value: "last8",
    label: siteTexts.ranking.page.periodOptions.last8.label,
    description: siteTexts.ranking.page.periodOptions.last8.description,
  },
  {
    value: "last30",
    label: siteTexts.ranking.page.periodOptions.last30.label,
    description: siteTexts.ranking.page.periodOptions.last30.description,
  },
  {
    value: "last90",
    label: siteTexts.ranking.page.periodOptions.last90.label,
    description: siteTexts.ranking.page.periodOptions.last90.description,
  },
  {
    value: "current_year",
    label: siteTexts.ranking.page.periodOptions.currentYear.label,
    description: siteTexts.ranking.page.periodOptions.currentYear.description,
  },
  {
    value: "custom",
    label: siteTexts.ranking.page.periodOptions.custom.label,
    description: siteTexts.ranking.page.periodOptions.custom.description,
  },
];

function categoryLabelFromCode(code: string | null | undefined) {
  return playerCategoryLabelFromCode(code);
}

function getRankingQueryParam(name: string) {
  if (typeof window === "undefined") return "";
  const hash = String(window.location.hash || "");
  const queryIndex = hash.indexOf("?");
  const query =
    queryIndex >= 0 ? hash.slice(queryIndex + 1) : window.location.search.slice(1);
  return String(new URLSearchParams(query).get(name) || "");
}

function normalizeRankingMode(value: string): RankingModeCode {
  return value === "super8" ||
    value === "challenge" ||
    value === "poco" ||
    value === "tournament"
    ? value
    : "all";
}

function normalizeRankingPeriod(value: string): RankingPeriodCode {
  return PERIOD_OPTIONS.some((option) => option.value === value)
    ? (value as RankingPeriodCode)
    : "all_time";
}

function normalizeRankingSource(value: string): RankingSourceCode {
  return value === "organizer" ? "organizer" : "official";
}

function normalizeRankingGender(value: string): RankingGenderCode {
  return value === "male" || value === "female" ? value : "all";
}

function normalizeRankingCategory(value: string) {
  return isPlayerCategoryCode(value) ? value : "open";
}

function renderRankingPoints(points: number) {
  return (
    <span className="responsive-ranking-table-points-label">
      <span className="responsive-ranking-table-points-value">{points}</span>
      <span className="responsive-ranking-table-points-unit">
        {siteTexts.ranking.shareModal.canvas.pointsSuffix}
      </span>
    </span>
  );
}

function rankingHistoryModalityLabel(value: string) {
  if (value === "tournament") return siteTexts.ranking.page.modeTabs.tournament;
  if (value === "super8") return siteTexts.ranking.page.modeTabs.super8;
  if (value === "poco") return siteTexts.ranking.page.modeTabs.poco;
  return siteTexts.ranking.page.modeTabs.challenge;
}

type RankingHistoryModalState = {
  playerName: string;
  cpf: string;
  loading: boolean;
  error: string;
  items: RankingUserHistoryItem[];
};

type RankingOrganizerOption = {
  id: number;
  label: string;
  sex?: "male" | "female" | "other" | null;
  avatarUrl?: string | null;
};

function OrganizerPicker({
  disabled = false,
  onChange,
  options,
  placeholder,
  value,
}: {
  disabled?: boolean;
  onChange: (value: string) => void;
  options: RankingOrganizerOption[];
  placeholder: string;
  value: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected = options.find((option) => String(option.id) === value);
  const selectedIsTnp = isTnpOrganizer(selected?.label);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`ranking-organizer-picker${open ? " is-open" : ""}`}
    >
      <button
        type="button"
        className={`ranking-organizer-picker-trigger${
          selectedIsTnp ? " has-tnp-logo" : ""
        }`}
        disabled={disabled}
        aria-label={selected?.label || placeholder}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((current) => !current)}
      >
        {selectedIsTnp ? (
          <TnpOrganizerLogo size="picker" />
        ) : selected ? (
          <UserAvatar
            name={selected.label}
            sex={selected.sex || ""}
            avatarUrl={selected.avatarUrl || ""}
            size={38}
          />
        ) : (
          <span className="material-symbols-outlined" aria-hidden="true">
            account_circle
          </span>
        )}
        <span>{selectedIsTnp ? "" : selected?.label || placeholder}</span>
        <span className="material-symbols-outlined" aria-hidden="true">
          expand_more
        </span>
      </button>
      {open && !disabled && (
        <div className="ranking-organizer-picker-menu" role="listbox">
          <button
            type="button"
            className={!value ? "is-selected" : ""}
            role="option"
            aria-selected={!value}
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              groups
            </span>
            <span>{placeholder}</span>
          </button>
          {options.map((organizer) => {
            const organizerIsTnp = isTnpOrganizer(organizer.label);
            return (
              <button
                key={`organizer-picker-${organizer.id}`}
                type="button"
                className={`${
                  String(organizer.id) === value ? "is-selected" : ""
                }${organizerIsTnp ? " has-tnp-logo" : ""}`}
                role="option"
                aria-label={organizer.label}
                aria-selected={String(organizer.id) === value}
                onClick={() => {
                  onChange(String(organizer.id));
                  setOpen(false);
                }}
              >
                {organizerIsTnp ? (
                  <TnpOrganizerLogo size="picker" />
                ) : (
                  <UserAvatar
                    name={organizer.label}
                    sex={organizer.sex || ""}
                    avatarUrl={organizer.avatarUrl || ""}
                    size={38}
                  />
                )}
                <span>{organizerIsTnp ? "" : organizer.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

type RankingPageProps = {
  forcedSource?: RankingSourceCode;
  forcedOrganizerUserId?: number;
};

export default function RankingPage({
  forcedSource,
  forcedOrganizerUserId,
}: RankingPageProps = {}) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [data, setData] = useState<Super8RankingResponse | null>(null);
  const [selectedMode, setSelectedMode] = useState<RankingModeCode>(() =>
    normalizeRankingMode(getRankingQueryParam("mode")),
  );
  const [selectedSource, setSelectedSource] = useState<RankingSourceCode>(
    forcedSource || normalizeRankingSource(getRankingQueryParam("source")),
  );
  const [selectedOrganizerUserId, setSelectedOrganizerUserId] = useState(
    Number.isFinite(forcedOrganizerUserId) && forcedOrganizerUserId > 0
      ? String(forcedOrganizerUserId)
      : getRankingQueryParam("organizerUserId"),
  );
  const [selectedCategory, setSelectedCategory] = useState(() =>
    normalizeRankingCategory(getRankingQueryParam("category")),
  );
  const [selectedGender, setSelectedGender] = useState<RankingGenderCode>(() =>
    normalizeRankingGender(getRankingQueryParam("gender")),
  );
  const [selectedPeriod, setSelectedPeriod] = useState<RankingPeriodCode>(() =>
    normalizeRankingPeriod(getRankingQueryParam("period")),
  );
  const [customDateFrom, setCustomDateFrom] = useState(() =>
    getRankingQueryParam("dateFrom"),
  );
  const [customDateTo, setCustomDateTo] = useState(() =>
    getRankingQueryParam("dateTo"),
  );
  const [selectedEventIds, setSelectedEventIds] = useState<number[] | null>(
    null,
  );
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
  const [historyModal, setHistoryModal] =
    useState<RankingHistoryModalState | null>(null);
  useBodyScrollLock(Boolean(historyModal));
  const [organizerDirectory, setOrganizerDirectory] = useState<
    RankingOrganizerOption[]
  >([]);
  const effectiveSource = forcedSource || selectedSource;
  const effectiveOrganizerUserId =
    Number.isFinite(forcedOrganizerUserId) && forcedOrganizerUserId > 0
      ? String(forcedOrganizerUserId)
      : selectedOrganizerUserId;
  useOrganizerPageVisualIdentity(
    data?.visualIdentity,
    effectiveSource === "organizer",
  );
  const showSourceSelector = !forcedSource;
  const showOrganizerSelectorOnly =
    forcedSource === "organizer" &&
    !(Number.isFinite(forcedOrganizerUserId) && forcedOrganizerUserId > 0);
  const info = siteTexts.common.info;
  const customDateRangeInvalid =
    selectedPeriod === "custom" &&
    isDateRangeInvalid(customDateFrom, customDateTo);

  useEffect(() => {
    if (forcedSource) {
      setSelectedSource(forcedSource);
    }
    if (Number.isFinite(forcedOrganizerUserId) && forcedOrganizerUserId > 0) {
      setSelectedOrganizerUserId(String(forcedOrganizerUserId));
    }
  }, [forcedOrganizerUserId, forcedSource]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = String(window.location.hash || "#/ranking/tnp");
    const [hashPathRaw] = hash.split("?");
    const hashPath = hashPathRaw || "#/ranking/tnp";
    const params = new URLSearchParams();
    params.set("mode", selectedMode);
    params.set("category", selectedCategory);
    params.set("gender", selectedGender);
    params.set("source", effectiveSource);
    params.set("period", selectedPeriod);
    if (selectedPeriod === "custom") {
      if (customDateFrom) params.set("dateFrom", customDateFrom);
      if (customDateTo) params.set("dateTo", customDateTo);
    }
    if (effectiveSource === "organizer" && effectiveOrganizerUserId) {
      params.set("organizerUserId", effectiveOrganizerUserId);
    }
    const query = params.toString();
    const nextUrl = window.location.pathname.startsWith("/s/ranking")
      ? `/s/ranking?${query}`
      : `${window.location.pathname}${window.location.search}${hashPath}?${query}`;
    window.history.replaceState(null, "", nextUrl);
  }, [
    effectiveOrganizerUserId,
    effectiveSource,
    customDateFrom,
    customDateTo,
    selectedCategory,
    selectedGender,
    selectedMode,
    selectedPeriod,
  ]);

  useEffect(() => {
    let cancelled = false;
    listOrganizers()
      .then((response) => {
        if (cancelled) return;
        const next = (response.items || [])
          .map((item) => ({
            id: Number(item.id || 0),
            label: String(item.name || "").trim(),
            sex: item.sex || null,
            avatarUrl: String(item.avatarUrl || "").trim() || null,
          }))
          .filter(
            (item) => Number.isFinite(item.id) && item.id > 0 && item.label,
          )
          .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
        setOrganizerDirectory(next);
      })
      .catch(() => {
        if (!cancelled) setOrganizerDirectory([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (customDateRangeInvalid) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        setLoadError("");
        const response = await getSuper8Ranking({
          mode: selectedMode,
          source: effectiveSource,
          category: selectedCategory,
          gender: selectedGender,
          period: selectedPeriod,
          dateFrom: selectedPeriod === "custom" ? customDateFrom : undefined,
          dateTo: selectedPeriod === "custom" ? customDateTo : undefined,
          eventIds:
            selectedPeriod === "all_time"
              ? undefined
              : selectedEventIds || undefined,
          organizerUserId:
            effectiveSource === "organizer" && effectiveOrganizerUserId
              ? Number(effectiveOrganizerUserId)
              : undefined,
        });
        if (cancelled) return;
        setData(response);
      } catch (_err) {
        if (cancelled) return;
        setData(null);
        setLoadError(
          _err instanceof Error
            ? _err.message
            : siteTexts.ranking.page.messages.loadError,
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [
    customDateFrom,
    customDateTo,
    customDateRangeInvalid,
    effectiveOrganizerUserId,
    effectiveSource,
    selectedCategory,
    selectedGender,
    selectedEventIds,
    selectedMode,
    selectedPeriod,
  ]);

  const rankingRows = useMemo<Super8RankingResponse["ranking"]>(() => {
    const allRows = data?.ranking || [];
    const selectedCategoryLabelForFilter =
      categoryLabelFromCode(selectedCategory).toLowerCase();
    const categoryFilteredRows = allRows.filter((row) => {
      const rowCategoryCode = String(row.categoryCode || "").toLowerCase();
      const rowCategoryLabel = String(row.categoryLabel || "").toLowerCase();
      const matchesCategory =
        rowCategoryCode === selectedCategory ||
        (!!selectedCategoryLabelForFilter &&
          rowCategoryLabel === selectedCategoryLabelForFilter);
      const matchesGender =
        selectedGender === "all" ||
        String(row.sex || "").toLowerCase() === selectedGender;
      return matchesCategory && matchesGender;
    });
    const filteredRows =
      selectedMode === "all"
        ? categoryFilteredRows
        : categoryFilteredRows.filter(
            (row) => String(row.modalityCode || "super8") === selectedMode,
          );
    const showAllCategoriesTogether = false;
    const selectedCategoryLabel = categoryLabelFromCode(selectedCategory);

    const aggregatedByCpf = new Map<
      string,
      {
        row: Super8RankingResponse["ranking"][number];
        categoryLabels: Set<string>;
      }
    >();
    filteredRows.forEach((row) => {
      const cpf = String(row.cpf || "").trim();
      if (!cpf) return;

      const categoryLabelRaw = String(row.categoryLabel || "").trim();
      const resolvedCategoryLabel =
        categoryLabelRaw && categoryLabelRaw.toLowerCase() !== "geral"
          ? categoryLabelRaw
          : categoryLabelFromCode(row.categoryCode) ||
            siteTexts.ranking.page.labels.uncategorized;
      const categoryKey = String(
        row.categoryCode || resolvedCategoryLabel,
      ).toLowerCase();
      const rowKey = showAllCategoriesTogether ? `${cpf}::${categoryKey}` : cpf;

      const existing = aggregatedByCpf.get(rowKey);
      if (!existing) {
        aggregatedByCpf.set(rowKey, {
          row: {
            ...row,
            id: `ranking::${rowKey}`,
            categoryLabel: resolvedCategoryLabel,
            points: Number(row.points || 0),
          },
          categoryLabels: new Set([resolvedCategoryLabel]),
        });
        return;
      }

      existing.row.points += Number(row.points || 0);
      if (!existing.row.playerName && row.playerName) {
        existing.row.playerName = row.playerName;
      }
      if (!existing.row.avatarUrl && row.avatarUrl) {
        existing.row.avatarUrl = row.avatarUrl;
      }
      if (!existing.row.sex && row.sex) {
        existing.row.sex = row.sex;
      }
      existing.categoryLabels.add(resolvedCategoryLabel);
    });

    return Array.from(aggregatedByCpf.values())
      .map(({ row, categoryLabels }) => {
        if (showAllCategoriesTogether) {
          return row;
        }
        const labels = Array.from(categoryLabels);
        if (labels.length === 1) {
          return { ...row, categoryLabel: labels[0] };
        }
        if (selectedCategoryLabel) {
          return {
            ...row,
            categoryCode: row.categoryCode || null,
            categoryLabel: selectedCategoryLabel,
          };
        }
        return row;
      })
      .sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        return String(a.playerName || "").localeCompare(
          String(b.playerName || ""),
        );
      })
      .map((row, index) => ({
        ...row,
        position: index + 1,
      }));
  }, [data, selectedCategory, selectedGender, selectedMode]);
  const organizerOptions = useMemo(() => {
    const map = new Map<number, RankingOrganizerOption>();
    organizerDirectory.forEach((item) => {
      map.set(item.id, item);
    });
    (data?.availableOrganizers || []).forEach((item) => {
      const id = Number(item.id || 0);
      const label = String(item.label || "").trim();
      if (!Number.isFinite(id) || id <= 0 || !label) return;
      const current = map.get(id);
      map.set(id, {
        id,
        label: current?.label || label,
        sex: current?.sex || item.sex || null,
        avatarUrl:
          current?.avatarUrl || String(item.avatarUrl || "").trim() || null,
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "pt-BR"),
    );
  }, [data?.availableOrganizers, organizerDirectory]);
  const selectedOrganizer = useMemo(() => {
    const organizerId = Number(effectiveOrganizerUserId || 0);
    return organizerOptions.find((item) => item.id === organizerId) || null;
  }, [effectiveOrganizerUserId, organizerOptions]);
  const rankingPageTitle = useMemo(() => {
    if (effectiveSource !== "organizer") {
      return siteTexts.ranking.page.titleFallbacks.ranking;
    }
    const organizerId = Number(effectiveOrganizerUserId || 0);
    if (!Number.isFinite(organizerId) || organizerId <= 0) {
      return siteTexts.ranking.page.titleFallbacks.organizerRanking;
    }
    const organizerName =
      organizerOptions.find((item) => item.id === organizerId)?.label || "";
    if (organizerName) {
      return `${siteTexts.ranking.page.titleFallbacks.ranking} ${organizerName}`;
    }
    return `${siteTexts.ranking.page.titleFallbacks.organizerById}${organizerId}`;
  }, [effectiveOrganizerUserId, effectiveSource, organizerOptions]);
  const selectedModeLabel = useMemo(
    () =>
      MODE_TABS.find((tab) => tab.value === selectedMode)?.label ||
      siteTexts.ranking.page.modeTabs.all,
    [selectedMode],
  );
  const selectedCategoryLabel = useMemo(
    () =>
      PLAYER_CATEGORY_OPTIONS.find((tab) => tab.value === selectedCategory)
        ?.label ||
      categoryLabelFromCode(selectedCategory) ||
      selectedCategory,
    [selectedCategory],
  );
  const selectedGenderLabel = useMemo(
    () =>
      selectedGender === "all"
        ? siteTexts.common.filters.allSexes
        : playerSexLabelFromCode(selectedGender) || selectedGender,
    [selectedGender],
  );
  const rankingShareSelectionLabel = useMemo(
    () =>
      [
        selectedGenderLabel,
        `${siteTexts.ranking.page.labels.category} ${selectedCategoryLabel}`,
        selectedModeLabel,
      ]
        .filter(Boolean)
        .join(" - "),
    [selectedCategoryLabel, selectedGenderLabel, selectedModeLabel],
  );
  const rankingDisplayTitle = useMemo(
    () => `${rankingPageTitle} - ${rankingShareSelectionLabel}`,
    [rankingPageTitle, rankingShareSelectionLabel],
  );
  const selectedPeriodOption = useMemo(
    () =>
      PERIOD_OPTIONS.find((option) => option.value === selectedPeriod) ||
      PERIOD_OPTIONS[0],
    [selectedPeriod],
  );
  const periodEvents = data?.period?.availableEvents || [];
  const rankingCategoryOptions = useMemo(() => {
    const availableCategories = data?.availableCategories || [];
    return availableCategories
      .map((category) => {
        const value = String(category.code || "").trim();
        const label =
          String(category.label || "").trim() ||
          playerCategoryLabelFromCode(value);
        if (!isPlayerCategoryCode(value) || !label) return null;
        return { value, label };
      })
      .filter(
        (option): option is (typeof PLAYER_CATEGORY_OPTIONS)[number] =>
          Boolean(option),
      );
  }, [data?.availableCategories]);
  const hasUnavailableRankingCategories = useMemo(() => {
    const allCategories = data?.categories || [];
    if (allCategories.length === 0) return false;
    const availableCodes = new Set(
      rankingCategoryOptions.map((option) => option.value),
    );
    return allCategories.some((category) => {
      const code = String(category.code || "").trim();
      return isPlayerCategoryCode(code) && !availableCodes.has(code);
    });
  }, [data?.categories, rankingCategoryOptions]);
  const rankingPageInfo = useMemo(
    () =>
      [
        siteTexts.ranking.page.messages.hint,
        hasUnavailableRankingCategories
          ? siteTexts.ranking.page.messages.availableCategoriesOnly
          : "",
      ]
        .filter(Boolean)
        .join(" "),
    [hasUnavailableRankingCategories],
  );
  const activeEventIds =
    selectedEventIds ||
    data?.period?.selectedEventIds ||
    periodEvents.map((event) => Number(event.id));
  const activeEventIdSet = useMemo(
    () => new Set(activeEventIds.map((id) => Number(id))),
    [activeEventIds],
  );
  const leader = rankingRows[0] || null;
  const summaryCards = useMemo(
    () => [
      {
        className: "ranking-summary-card--leader",
        icon: "emoji_events",
        label: siteTexts.ranking.page.summaryCards.leader,
        value: leader?.playerName || siteTexts.ranking.page.summaryCards.noLeader,
        hint: leader
          ? `${Number(leader.points || 0)} ${
              siteTexts.ranking.shareModal.canvas.pointsSuffix
            }`
          : siteTexts.ranking.page.summaryCards.waitingRanking,
      },
      {
        className: "ranking-summary-card--events",
        icon: "sports_tennis",
        label: siteTexts.ranking.page.summaryCards.countedEvents,
        value: Number(data?.totals?.events || 0),
        hint:
          selectedPeriod === "all_time"
            ? siteTexts.ranking.page.summaryCards.allHistory
            : siteTexts.ranking.page.summaryCards.periodEvents,
      },
      {
        className: "ranking-summary-card--athletes",
        icon: "groups",
        label: siteTexts.ranking.page.summaryCards.rankedAthletes,
        value: rankingRows.length,
        hint: siteTexts.ranking.page.summaryCards.currentSelection,
      },
      {
        className: "ranking-summary-card--period",
        icon: "calendar_month",
        label: siteTexts.ranking.page.summaryCards.currentPeriod,
        value: selectedPeriodOption.label,
        hint: selectedPeriodOption.description,
      },
    ],
    [
      data?.totals?.events,
      leader,
      rankingRows.length,
      selectedPeriod,
      selectedPeriodOption,
    ],
  );
  const rankingShareUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("mode", selectedMode);
    params.set("category", selectedCategory);
    params.set("gender", selectedGender);
    params.set("source", effectiveSource);
    params.set("period", selectedPeriod);
    if (selectedPeriod === "custom") {
      if (customDateFrom) params.set("dateFrom", customDateFrom);
      if (customDateTo) params.set("dateTo", customDateTo);
    }
    if (effectiveSource === "organizer" && effectiveOrganizerUserId) {
      params.set("organizerUserId", effectiveOrganizerUserId);
    }
    return buildRankingShareUrl(params);
  }, [
    effectiveOrganizerUserId,
    effectiveSource,
    customDateFrom,
    customDateTo,
    selectedCategory,
    selectedGender,
    selectedMode,
    selectedPeriod,
  ]);

  useEffect(() => {
    syncCanonicalShareUrl(rankingShareUrl);
  }, [rankingShareUrl]);

  const openRankingHistory = useCallback(
    async (row: Super8RankingResponse["ranking"][number]) => {
      const cpf = String(row.cpf || "").trim();
      const playerName =
        String(row.playerName || "").trim() ||
        siteTexts.ranking.page.messages.athleteFallback;
      if (!cpf) return;
      setHistoryModal({
        playerName,
        cpf,
        loading: true,
        error: "",
        items: [],
      });
      try {
        const response = await getRankingUserHistory(cpf, {
          category: selectedCategory || undefined,
          mode: selectedMode === "all" ? undefined : selectedMode,
          source: effectiveSource,
          organizerUserId:
            effectiveSource === "organizer" && effectiveOrganizerUserId
              ? Number(effectiveOrganizerUserId)
              : undefined,
        });
        const history =
          selectedMode === "all"
            ? response.history || []
            : (response.history || []).filter(
                (item) => item.competitionType === selectedMode,
              );
        setHistoryModal({
          playerName: response.playerName || playerName,
          cpf: response.cpf || cpf,
          loading: false,
          error: "",
          items: history,
        });
      } catch (_err) {
        setHistoryModal({
          playerName,
          cpf,
          loading: false,
          error:
            _err instanceof Error
              ? _err.message
              : siteTexts.ranking.page.messages.historyLoadError,
          items: [],
        });
      }
    },
    [effectiveOrganizerUserId, effectiveSource, selectedCategory, selectedMode],
  );
  const columns = useMemo<
    Array<ResponsiveRankingColumn<Super8RankingResponse["ranking"][number]>>
  >(
    () => [
      {
        id: "points",
        header: (
          <span className="info-label">
            {siteTexts.ranking.page.labels.points}
            <InfoTooltip content={info.points} />
          </span>
        ),
        mobileLabel: (
          <span className="info-label">
            {siteTexts.ranking.page.labels.points}
            <InfoTooltip content={info.points} />
          </span>
        ),
        render: (row) => renderRankingPoints(row.points),
        cellClassName: "responsive-ranking-table-points",
      },
    ],
    [info.points],
  );
  const rows = useMemo<
    Array<ResponsiveRankingRow<Super8RankingResponse["ranking"][number]>>
  >(
    () =>
      rankingRows.map((row, index) => ({
        id: row.id,
        rank: row.position || index + 1,
        profileHref: row.publicPlayerId
          ? `#/jogadores/${row.publicPlayerId}`
          : undefined,
        title: row.publicPlayerId ? (
          <a
            className="ranking-profile-link"
            href={`#/jogadores/${row.publicPlayerId}`}
            onClick={(event) => event.stopPropagation()}
          >
            {row.playerName}
          </a>
        ) : (
          <span className="ranking-history-trigger">{row.playerName}</span>
        ),
        avatars: [
          {
            name: row.playerName,
            avatarUrl: row.avatarUrl,
            sex: row.sex,
          },
        ],
        summaryValue: renderRankingPoints(row.points),
        data: row,
      })),
    [rankingRows],
  );

  useEffect(() => {
    if (rankingCategoryOptions.length === 0) return;
    if (rankingCategoryOptions.some((option) => option.value === selectedCategory)) {
      return;
    }
    setSelectedCategory(rankingCategoryOptions[0].value);
    setSelectedEventIds(null);
  }, [rankingCategoryOptions, selectedCategory]);

  if (loading) {
    return <LoadingPage label={siteTexts.common.loading} />;
  }

  const organizerPageStyle =
    effectiveSource === "organizer"
      ? organizerIdentityStyle(data?.visualIdentity)
      : undefined;

  return (
    <AppLayout
      title={
        <span
          className={`ranking-page-title${
            effectiveSource === "organizer" ? " is-organizer" : ""
          }`}
          style={organizerPageStyle}
        >
          <span className="ranking-page-title__main">
            {selectedOrganizer && isTnpOrganizer(selectedOrganizer.label) ? (
              <TnpOrganizerLogo size="title" />
            ) : selectedOrganizer && data?.visualIdentity?.logoUrl ? (
              <img
                className="ranking-page-organizer-logo"
                src={data.visualIdentity.logoUrl}
                alt=""
              />
            ) : selectedOrganizer ? (
              <UserAvatar
                name={selectedOrganizer.label}
                sex={selectedOrganizer.sex || ""}
                avatarUrl={selectedOrganizer.avatarUrl || ""}
                size={54}
              />
            ) : null}
            <span>{rankingDisplayTitle}</span>
            <InfoTooltip
              className="ranking-page-title-info"
              content={rankingPageInfo}
            />
          </span>
        </span>
      }
      headerRight={
        <GradientShareButton
          className="ranking-share-trigger"
          icon="ios_share"
          onClick={() => setShareModalOpen(true)}
        >
          {siteTexts.ranking.shareModal.shareAction}
        </GradientShareButton>
      }
    >
      <div
        className={`ranking-super8-page${
          mobileFiltersOpen ? " is-mobile-filters-open" : ""
        }${mobileInfoOpen ? " is-mobile-info-open" : ""}${
          effectiveSource === "organizer" ? " is-organizer" : ""
        }`}
        style={organizerPageStyle}
      >
        {data && (
          <div className="ranking-super8-topbar">
            {selectedSource === "organizer" && data.scoringStandard && (
              <div className="ranking-scoring-standard">
                <span>{siteTexts.ranking.page.labels.scoringStandard}</span>
                <strong>
                  {data.scoringStandard.label}
                  {data.scoringStandard.isCustom && (
                    <InfoTooltip
                      content={
                        siteTexts.ranking.page.messages.customScoringInfo
                      }
                    />
                  )}
                </strong>
              </div>
            )}
            <div
              className="ranking-mobile-panel-toggles"
              aria-label={siteTexts.ranking.page.labels.mobileControls}
            >
              <Button
                type="button"
                variant="pill"
                className="ranking-mobile-panel-toggle"
                aria-expanded={mobileFiltersOpen}
                onClick={() => setMobileFiltersOpen((current) => !current)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  tune
                </span>
                {siteTexts.ranking.page.labels.filters}
              </Button>
              <Button
                type="button"
                variant="pill"
                className="ranking-mobile-panel-toggle"
                aria-expanded={mobileInfoOpen}
                onClick={() => setMobileInfoOpen((current) => !current)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  info
                </span>
                {siteTexts.ranking.page.labels.moreInformation}
              </Button>
            </div>
          </div>
        )}
        {data && (
          <div className="ranking-super8-toolbar">
            <div className="ranking-filter-board">
              <section className="ranking-filter-section ranking-filter-section--player-filters">
                <PlayerFiltersBar
                  className="ranking-player-filters"
                  ariaLabel={siteTexts.ranking.page.labels.filters}
                  categoryLabel={siteTexts.ranking.page.labels.category}
                  categoryValue={selectedCategory}
                  categoryIncludeAll={false}
                  categoryOptions={rankingCategoryOptions}
                  onCategoryChange={(value) => {
                    setSelectedCategory(normalizeRankingCategory(value));
                    setSelectedEventIds(null);
                  }}
                  sexLabel={siteTexts.admin.createChallengePage.gender}
                  sexValue={selectedGender}
                  sexAllValue="all"
                  onSexChange={(value) => {
                    setSelectedGender(
                      normalizeRankingGender(value),
                    );
                    setSelectedEventIds(null);
                  }}
                />
              </section>
              <section className="ranking-filter-section">
                <span className="ranking-filter-title">
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    sports_tennis
                  </span>
                  {siteTexts.ranking.page.labels.modality}
                  <InfoTooltip content={info.modality} />
                </span>
                <label className="ranking-filter-select ranking-mode-select">
                  <AppSelect
                    value={selectedMode}
                    onChange={(event) => {
                      setSelectedMode(event.target.value as RankingModeCode);
                      setSelectedEventIds(null);
                    }}
                  >
                    {MODE_TABS.map((tab) => (
                      <option
                        key={`ranking-mode-option-${tab.value}`}
                        value={tab.value}
                      >
                        {tab.label}
                      </option>
                    ))}
                  </AppSelect>
                </label>
              </section>
              <section className="ranking-filter-section ranking-filter-section--period">
                <span className="ranking-filter-title">
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    calendar_month
                  </span>
                  {siteTexts.ranking.page.labels.period}
                  <InfoTooltip content={info.period} />
                </span>
                <label className="ranking-filter-select ranking-period-select">
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    calendar_month
                  </span>
                  <AppSelect
                    value={selectedPeriod}
                    onChange={(event) => {
                      setSelectedPeriod(
                        event.target.value as RankingPeriodCode,
                      );
                      setSelectedEventIds(null);
                    }}
                  >
                    {PERIOD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </AppSelect>
                </label>
              </section>
            </div>
            {selectedPeriod === "custom" && (
              <div className="ranking-custom-period">
                <OrganizerDatePicker
                  label={siteTexts.ranking.page.labels.dateFrom}
                  value={customDateFrom}
                  disabledAfter={customDateTo}
                  onChange={(value) => {
                    setCustomDateFrom(value);
                    setSelectedEventIds(null);
                  }}
                />
                <OrganizerDatePicker
                  label={siteTexts.ranking.page.labels.dateTo}
                  value={customDateTo}
                  disabledBefore={customDateFrom}
                  onChange={(value) => {
                    setCustomDateTo(value);
                    setSelectedEventIds(null);
                  }}
                />
                {customDateRangeInvalid && (
                  <p className="organizer-stat-date-error">
                    {siteTexts.ranking.page.messages.invalidDateRange}
                  </p>
                )}
              </div>
            )}
            {showSourceSelector && (
              <div className="ranking-source-row">
                <label className="field ranking-source-field">
                  <span>{siteTexts.ranking.page.labels.source}</span>
                  <AppSelect
                    value={selectedSource}
                    onChange={(event) => {
                      setSelectedSource(
                        event.target.value as RankingSourceCode,
                      );
                      setSelectedEventIds(null);
                    }}
                  >
                    {SOURCE_TABS.map((tab) => (
                      <option
                        key={`ranking-source-option-${tab.value}`}
                        value={tab.value}
                      >
                        {tab.label}
                      </option>
                    ))}
                  </AppSelect>
                </label>
                <div className="field ranking-source-field">
                  <span>{siteTexts.ranking.page.labels.organizer}</span>
                  <OrganizerPicker
                    value={selectedOrganizerUserId}
                    onChange={(nextValue) => {
                      setSelectedOrganizerUserId(nextValue);
                      setSelectedEventIds(null);
                    }}
                    disabled={selectedSource !== "organizer"}
                    options={organizerOptions}
                    placeholder={
                      selectedSource === "organizer"
                        ? siteTexts.ranking.page.labels.allOrganizers
                        : siteTexts.ranking.page.labels.disabledOrganizerOption
                    }
                  />
                </div>
              </div>
            )}
            {showOrganizerSelectorOnly && (
              <div className="ranking-source-row">
                <div className="field ranking-source-field">
                  <span>{siteTexts.ranking.page.labels.organizer}</span>
                  <OrganizerPicker
                    value={selectedOrganizerUserId}
                    onChange={(nextValue) => {
                      setSelectedOrganizerUserId(nextValue);
                      setSelectedEventIds(null);
                    }}
                    options={organizerOptions}
                    placeholder={siteTexts.ranking.page.labels.allOrganizers}
                  />
                </div>
              </div>
            )}
            {showOrganizerSelectorOnly && organizerOptions.length === 0 && (
              <p className="status-message">
                {siteTexts.ranking.page.messages.noOrganizerRankings}
              </p>
            )}
            {selectedPeriod !== "all_time" && (
              <section className="ranking-stage-panel">
                <div className="ranking-stage-panel-head">
                  <div>
                    <span className="ranking-filter-title">
                      {siteTexts.ranking.page.labels.consideredStages}
                      <InfoTooltip content={info.consideredStages} />
                    </span>
                    <p>{selectedPeriodOption.description}</p>
                  </div>
                  <Button
                    type="button"
                    variant="pill"
                    className="ranking-stage-select-all"
                    onClick={() =>
                      setSelectedEventIds(
                        periodEvents.map((event) => Number(event.id)),
                      )
                    }
                  >
                    {siteTexts.ranking.page.labels.selectAll}
                  </Button>
                </div>
                <div className="ranking-stage-list">
                  {periodEvents.map((event, index) => {
                    const id = Number(event.id);
                    const checked = activeEventIdSet.has(id);
                    return (
                      <label className="ranking-stage-item" key={id}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const base =
                              selectedEventIds ||
                              periodEvents.map((item) => Number(item.id));
                            setSelectedEventIds(
                              checked
                                ? base.filter((itemId) => itemId !== id)
                                : [...base, id],
                            );
                          }}
                        />
                        <span>
                          {event.label ||
                            `${siteTexts.ranking.page.labels.stageFallback} ${
                              index + 1
                            }`}
                        </span>
                      </label>
                    );
                  })}
                  {periodEvents.length === 0 && (
                    <p className="ranking-stage-empty">
                      {siteTexts.ranking.page.messages.noStagesForPeriod}
                    </p>
                  )}
                </div>
              </section>
            )}
            <div className="ranking-summary-grid">
              {summaryCards.map((card) => (
                <article
                  className={`ranking-summary-card ${card.className}`}
                  key={card.label}
                >
                  <span className="ranking-summary-icon" aria-hidden="true">
                    <span className="material-symbols-outlined">
                      {card.icon}
                    </span>
                  </span>
                  <div>
                    <span className="info-label">
                      {card.label}
                      {card.className.includes("events") && (
                        <InfoTooltip content={info.countedEvents} />
                      )}
                      {card.className.includes("athletes") && (
                        <InfoTooltip content={info.rankedAthletes} />
                      )}
                      {card.className.includes("period") && (
                        <InfoTooltip content={info.period} />
                      )}
                    </span>
                    <strong>{card.value}</strong>
                    <small>{card.hint}</small>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
        {loadError && <p className="status-message">{loadError}</p>}

        <ResponsiveRankingTable
          isRanking
          className="ranking-modern-table"
          ariaLabel={siteTexts.ranking.page.ariaLabel}
          rankHeader={siteTexts.ranking.page.labels.rank}
          titleHeader={
            <span className="info-label">
              {siteTexts.ranking.page.labels.name}
              <InfoTooltip content={info.rankedAthletes} />
            </span>
          }
          columns={columns}
          rows={rows}
          onRowClick={(row) => void openRankingHistory(row)}
          getRowActionLabel={(row) =>
            `${siteTexts.ranking.page.messages.viewHistoryPrefix} ${
              row.playerName || siteTexts.ranking.page.messages.athleteFallback
            }`
          }
          emptyMessage={siteTexts.ranking.page.messages.empty}
          emptyState={
            <EmptyState
              title={siteTexts.ranking.page.messages.emptyFinishedTitle}
              description={siteTexts.ranking.page.messages.emptyFinishedHint}
              iconName="leaderboard"
            />
          }
        />
        <RankingShareModal
          open={shareModalOpen}
          rankingName={selectedModeLabel}
          categoryLabel={selectedCategoryLabel}
          selectionLabel={rankingShareSelectionLabel}
          rankingRows={rankingRows}
          shareUrl={rankingShareUrl}
          visualIdentity={
            selectedSource === "organizer" ? data?.visualIdentity : null
          }
          onClose={() => setShareModalOpen(false)}
        />
        {historyModal && (
          <div
            className="ranking-history-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setHistoryModal(null);
            }}
          >
            <section
              className="ranking-history-modal"
              role="dialog"
              aria-modal="true"
              aria-label={`${siteTexts.ranking.page.messages.historyAriaPrefix} ${historyModal.playerName}`}
            >
              <header className="ranking-history-head">
                <div>
                  <span className="ranking-history-kicker">
                    {siteTexts.ranking.page.messages.historyTitle}
                  </span>
                  <h2>{historyModal.playerName}</h2>
                </div>
                <button
                  type="button"
                  className="ranking-history-close"
                  aria-label={siteTexts.ranking.page.messages.closeHistory}
                  onClick={() => setHistoryModal(null)}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    close
                  </span>
                </button>
              </header>
              {historyModal.loading ? (
                <div className="ranking-history-state">
                  {siteTexts.common.loading}
                </div>
              ) : historyModal.error ? (
                <div className="ranking-history-state">
                  {historyModal.error}
                </div>
              ) : historyModal.items.length === 0 ? (
                <div className="ranking-history-state">
                  {siteTexts.ranking.page.messages.emptyHistory}
                </div>
              ) : (
                <div className="ranking-history-list">
                  {historyModal.items.map((item) => (
                    <article
                      className="ranking-history-item"
                      key={`${item.competitionId}-${item.competitionType}-${item.competitionDate || ""}`}
                    >
                      <div className="ranking-history-item-main">
                        <span>
                          {rankingHistoryModalityLabel(item.competitionType)}
                        </span>
                        {item.categoryName ? (
                          <small className="ranking-history-category">
                            {item.categoryName}
                          </small>
                        ) : null}
                        {item.detailUrl ? (
                          <a
                            className="ranking-history-competition-link"
                            href={item.detailUrl}
                            onClick={() => setHistoryModal(null)}
                          >
                            {item.competitionName}
                          </a>
                        ) : (
                          <strong>{item.competitionName}</strong>
                        )}
                      </div>
                      <div className="ranking-history-item-stats">
                        <span>{item.resultLabel || item.placement}</span>
                        <strong>
                          {Number(item.pointsEarned || 0)}{" "}
                          {siteTexts.ranking.shareModal.canvas.pointsSuffix}
                        </strong>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
